// src/screens/RankingScreen.tsx - ランキング画面（改善版・売上基準）

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  IconButton,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Switch,
  FormControlLabel,
  Alert,
  Avatar
} from '@mui/material';
import {
  EmojiEvents,
  ThumbUp,
  ThumbDown,
  Settings,
  Person,
  TrendingUp,
  Paid,
  LocalShipping
} from '@mui/icons-material';
import {
  getRankingSettings,
  setRankingSettings,
  getRankingRecords,
  getMyRankingRecords,
  submitRankingRecord,
  voteRecord,
  hasVoted,
  RankingSettings,
  RankingRecord,
  RankingPeriod,
  RankingEntry
} from '../services/rankingService';
import { getAllDeliveries } from '../services/dataService';
import { DeliveryRecord, calculateActualWorkingMinutes } from '../types';

interface Props {
  onShowSnackbar: (message: string, severity?: 'success' | 'error') => void;
  simpleMode?: boolean;
}

function RankingScreen({ onShowSnackbar, simpleMode = false }: Props) {
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState<RankingSettings>({ isEnabled: false, userName: '', region: '' });
  const [period, setPeriod] = useState<RankingPeriod>('today');
  const [rankings, setRankings] = useState<RankingEntry[]>([]);
  const [myRecords, setMyRecords] = useState<RankingRecord[]>([]);
  const [showSettings, setShowSettings] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [votedRecords, setVotedRecords] = useState<Map<string, 'good' | 'bad'>>(new Map());

  const [tempEnabled, setTempEnabled] = useState(false);
  const [tempUserName, setTempUserName] = useState('');
  const [tempRegion, setTempRegion] = useState('');

  const regions = [
    '北海道', '青森県', '岩手県', '宮城県', '秋田県', '山形県', '福島県',
    '茨城県', '栃木県', '群馬県', '埼玉県', '千葉県', '東京都', '神奈川県',
    '新潟県', '富山県', '石川県', '福井県', '山梨県', '長野県',
    '岐阜県', '静岡県', '愛知県', '三重県',
    '滋賀県', '京都府', '大阪府', '兵庫県', '奈良県', '和歌山県',
    '鳥取県', '島根県', '岡山県', '広島県', '山口県',
    '徳島県', '香川県', '愛媛県', '高知県',
    '福岡県', '佐賀県', '長崎県', '熊本県', '大分県', '宮崎県', '鹿児島県',
    '沖縄県'
  ];

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    loadRankings();
  }, [period]);

  const loadData = async () => {
    try {
      setLoading(true);
      const settingsData = await getRankingSettings();
      setSettings(settingsData);
      setTempEnabled(settingsData.isEnabled);
      setTempUserName(settingsData.userName);
      setTempRegion(settingsData.region || '');

      await loadRankings();
      
      if (settingsData.isEnabled) {
        const myData = await getMyRankingRecords();
        setMyRecords(myData);
      }
    } catch (error) {
      console.error('データ読み込みエラー:', error);
      onShowSnackbar('データの読み込みに失敗しました', 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadRankings = async () => {
    try {
      const records = await getRankingRecords(period);
      
      // 売上でソートしてランキングを作成
      const sorted = records
        .map((r: RankingRecord) => ({
          ...r,
          rank: 0,
          hourlyRate: r.workingMinutes > 0 ? Math.floor((r.revenue / r.workingMinutes) * 60) : 0,
          isCurrentUser: false
        }))
        .sort((a: RankingEntry, b: RankingEntry) => b.revenue - a.revenue); // 売上で比較

      // ランクを付与
      sorted.forEach((entry: RankingEntry, index: number) => {
        entry.rank = index + 1;
      });

      setRankings(sorted);

      // 投票状態を読み込み
      const voteMap = new Map<string, 'good' | 'bad'>();
      for (const record of sorted) {
        if (record.id) {
          const voteStatus = await hasVoted(record.id);
          if (voteStatus.voted && voteStatus.type) {
            voteMap.set(record.id, voteStatus.type);
          }
        }
      }
      setVotedRecords(voteMap);
    } catch (error) {
      console.error('ランキング読み込みエラー:', error);
    }
  };

  const handleOpenSettings = () => {
    setTempEnabled(settings.isEnabled);
    setTempUserName(settings.userName);
    setTempRegion(settings.region || '');
    
    if (!settings.isEnabled) {
      setShowTerms(true);
    } else {
      setShowSettings(true);
    }
  };

  const handleAgreeTerms = () => {
    setShowTerms(false);
    setTempEnabled(true);
    setShowSettings(true);
  };

  const handleSaveSettings = async () => {
    try {
      if (tempEnabled && !tempUserName.trim()) {
        onShowSnackbar('表示名を入力してください', 'error');
        return;
      }

      if (tempEnabled && !tempRegion.trim()) {
        onShowSnackbar('地域を選択してください', 'error');
        return;
      }

      const newSettings: RankingSettings = {
        isEnabled: tempEnabled,
        userName: tempUserName.trim(),
        region: tempRegion.trim()
      };

      await setRankingSettings(newSettings);
      setSettings(newSettings);
      
      if (tempEnabled && !settings.isEnabled) {
        await submitAllExistingRecords();
      }
      
      setShowSettings(false);
      onShowSnackbar('設定を保存しました');
      
      if (newSettings.isEnabled) {
        await loadRankings();
      }
    } catch (error) {
      onShowSnackbar('設定の保存に失敗しました', 'error');
    }
  };

  const submitAllExistingRecords = async () => {
    try {
      onShowSnackbar('既存の記録をランキングに送信中...', 'success');
      
      const deliveries = await getAllDeliveries();
      
      if (deliveries.length === 0) {
        onShowSnackbar('送信する記録がありません', 'success');
        return;
      }

      const dateGroups = new Map<string, DeliveryRecord[]>();
      deliveries.forEach((d: DeliveryRecord) => {
        const group = dateGroups.get(d.date) || [];
        group.push(d);
        dateGroups.set(d.date, group);
      });

      let successCount = 0;
      let errorCount = 0;

      for (const [date, dayDeliveries] of Array.from(dateGroups.entries())) {
        const platformGroups = new Map<string, DeliveryRecord[]>();
        dayDeliveries.forEach((d: DeliveryRecord) => {
          const group = platformGroups.get(d.platform) || [];
          group.push(d);
          platformGroups.set(d.platform, group);
        });

        for (const [platform, records] of Array.from(platformGroups.entries())) {
          try {
            const revenue = records.reduce((sum: number, r: DeliveryRecord) => sum + r.totalRevenue, 0);
            const deliveryCount = records.reduce((sum: number, r: DeliveryRecord) => sum + r.deliveryCount, 0);
            const workingMinutes = calculateActualWorkingMinutes(records);

            await submitRankingRecord({
              userName: tempUserName.trim(),
              date,
              revenue,
              deliveryCount,
              workingMinutes,
              platform,
              createdAt: Date.now()
            });
            
            successCount++;
          } catch (error) {
            console.error('記録送信エラー:', error);
            errorCount++;
          }
        }
      }

      if (errorCount > 0) {
        onShowSnackbar(`${successCount}件送信完了、${errorCount}件失敗しました`, 'error');
      } else {
        onShowSnackbar(`${successCount}件の記録をランキングに送信しました`, 'success');
      }
    } catch (error) {
      console.error('既存記録送信エラー:', error);
      onShowSnackbar('記録の送信中にエラーが発生しました', 'error');
    }
  };

  const handleVote = async (recordId: string, voteType: 'good' | 'bad') => {
    try {
      await voteRecord(recordId, voteType);
      setVotedRecords(prev => new Map(prev).set(recordId, voteType));
      await loadRankings();
      onShowSnackbar(voteType === 'good' ? 'グッドを送りました' : 'バッドを送りました');
    } catch (error: any) {
      onShowSnackbar(error.message || '投票に失敗しました', 'error');
    }
  };

  const formatCurrency = (value: number) => `¥${value.toLocaleString()}`;

  const getPeriodLabel = (period: RankingPeriod) => {
    switch (period) {
      case 'today': return '今日';
      case 'week': return '今週';
      case 'month': return '今月';
    }
  };

  const getRankColor = (rank: number) => {
    if (rank === 1) return '#FFD700';
    if (rank === 2) return '#C0C0C0';
    if (rank === 3) return '#CD7F32';
    return '#888';
  };

  const getRankDisplay = (rank: number) => {
    if (rank === 1) return { icon: '🥇', bg: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)' };
    if (rank === 2) return { icon: '🥈', bg: 'linear-gradient(135deg, #C0C0C0 0%, #808080 100%)' };
    if (rank === 3) return { icon: '🥉', bg: 'linear-gradient(135deg, #CD7F32 0%, #8B4513 100%)' };
    return { icon: `${rank}位`, bg: '#2a2a2a' };
  };

  if (simpleMode) {
    return (
      <Box sx={{ p: 2, pb: 10, bgcolor: '#0b0f0d', minHeight: '100vh' }}>
        <Card sx={{ bgcolor: '#121713', border: '1px solid #26312b', borderRadius: '14px' }}>
          <CardContent sx={{ p: 2.5 }}>
            <Typography fontSize={22} fontWeight={800} color="white" sx={{ mb: 1 }}>
              ランキングは非表示中です
            </Typography>
            <Typography fontSize={15} color="#a7b0aa" lineHeight={1.6}>
              シンプルモードでは、売上管理に集中できるようランキングや時給比較を隠しています。
              ランキングを使う場合は、ホームでシンプルモードをオフにしてください。
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', bgcolor: '#0a0a0a' }}>
        <LinearProgress sx={{ width: '80%', bgcolor: '#1a4032', '& .MuiLinearProgress-bar': { bgcolor: '#10B981' } }} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, pb: 10, bgcolor: '#0a0a0a', minHeight: '100vh' }}>
      {/* ヘッダー */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight="bold" color="white" sx={{ mb: 0.5 }}>
            🏆 売上ランキング
          </Typography>
          <Typography fontSize={14} color="#888">
            全国の配達員と競争しよう
          </Typography>
        </Box>
        <IconButton onClick={handleOpenSettings} sx={{ color: '#888' }}>
          <Settings />
        </IconButton>
      </Box>

      {/* 説明カード */}
      <Card sx={{ mb: 3, bgcolor: '#1a1a1a', borderRadius: '12px' }}>
        <CardContent sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ fontSize: 32 }}>📊</Box>
            <Box sx={{ flex: 1 }}>
              <Typography fontSize={15} fontWeight="bold" color="white" sx={{ mb: 0.5 }}>
                1日の総売上で順位が決まります
              </Typography>
              <Typography fontSize={13} color="#888">
                グッド👍/バッド👎で不正な記録を排除できます
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* 参加状態の表示 */}
      {!settings.isEnabled && (
        <Card 
          sx={{ 
            mb: 3, 
            bgcolor: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
            background: 'linear-gradient(135deg, #10B98140 0%, #05966940 100%)',
            borderRadius: '16px',
            border: '2px solid #10B981',
            cursor: 'pointer',
            transition: 'transform 0.2s',
            '&:hover': { transform: 'scale(1.02)' }
          }}
          onClick={handleOpenSettings}
        >
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: '12px',
                  bgcolor: '#10B981',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 28
                }}
              >
                🏆
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography fontSize={17} fontWeight="bold" color="white" sx={{ mb: 0.5 }}>
                  ランキングに参加しよう！
                </Typography>
                <Typography fontSize={15} color="#ccc">
                  全国の配達員と競い合って、モチベーションUP
                </Typography>
              </Box>
              <Box
                sx={{
                  bgcolor: '#10B981',
                  color: 'black',
                  px: 3,
                  py: 1.5,
                  borderRadius: '12px',
                  fontWeight: 'bold',
                  fontSize: 14
                }}
              >
                今すぐ参加 →
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* 期間選択 */}
      <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
        {(['today', 'week', 'month'] as RankingPeriod[]).map((p) => (
          <Chip
            key={p}
            label={getPeriodLabel(p)}
            onClick={() => setPeriod(p)}
            sx={{
              flex: 1,
              height: 44,
              fontSize: 15,
              fontWeight: 'bold',
              bgcolor: period === p ? '#10B981' : '#2a2a2a',
              color: period === p ? 'black' : 'white',
              '&:hover': { bgcolor: period === p ? '#0d9668' : '#333' }
            }}
          />
        ))}
      </Box>

      {/* ランキングリスト */}
      {rankings.length === 0 ? (
        <Card sx={{ bgcolor: '#1a1a1a', borderRadius: '16px', p: 4 }}>
          <Box sx={{ textAlign: 'center', py: 4, color: '#888' }}>
            <EmojiEvents sx={{ fontSize: 60, mb: 2, opacity: 0.3 }} />
            <Typography fontSize={16}>
              {getPeriodLabel(period)}のランキングデータがありません
            </Typography>
          </Box>
        </Card>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {rankings.map((entry) => {
            const rankDisplay = getRankDisplay(entry.rank);
            return (
              <Card 
                key={entry.id} 
                sx={{ 
                  bgcolor: '#1a1a1a', 
                  borderRadius: '16px',
                  border: entry.rank <= 3 ? `2px solid ${getRankColor(entry.rank)}40` : 'none',
                  transition: 'transform 0.2s',
                  '&:hover': { transform: 'translateY(-2px)' }
                }}
              >
                <CardContent sx={{ p: 2.5 }}>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    {/* 順位バッジ */}
                    <Box
                      sx={{
                        minWidth: 60,
                        height: 60,
                        borderRadius: '12px',
                        background: rankDisplay.bg,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: entry.rank <= 3 ? 32 : 18,
                        fontWeight: 'bold',
                        color: entry.rank <= 3 ? 'white' : '#888',
                        boxShadow: entry.rank <= 3 ? `0 4px 12px ${getRankColor(entry.rank)}40` : 'none'
                      }}
                    >
                      {rankDisplay.icon}
                    </Box>

                    {/* メイン情報 */}
                    <Box sx={{ flex: 1 }}>
                      {/* ユーザー名と地域 */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Typography fontSize={17} fontWeight="bold" color="white">
                          {entry.userName}
                        </Typography>
                        <Chip
                          label={entry.region}
                          size="small"
                          sx={{ 
                            bgcolor: '#2a2a2a', 
                            color: '#10B981', 
                            fontSize: 11,
                            height: 22
                          }}
                        />
                        <Chip
                          label={entry.platform}
                          size="small"
                          sx={{ 
                            bgcolor: '#2a2a2a', 
                            color: '#888', 
                            fontSize: 11,
                            height: 22
                          }}
                        />
                      </Box>

                      {/* 売上・件数・時給 */}
                      <Box sx={{ display: 'flex', gap: 3, mb: 1.5 }}>
                        <Box>
                          <Typography fontSize={13} color="#888" sx={{ mb: 0.3 }}>
                            売上
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Paid sx={{ fontSize: 18, color: '#10B981' }} />
                            <Typography fontSize={18} fontWeight="bold" color="#10B981">
                              {formatCurrency(entry.revenue)}
                            </Typography>
                          </Box>
                        </Box>
                        <Box>
                          <Typography fontSize={13} color="#888" sx={{ mb: 0.3 }}>
                            配達件数
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <LocalShipping sx={{ fontSize: 16, color: '#3B82F6' }} />
                            <Typography fontSize={17} fontWeight="bold" color="white">
                              {entry.deliveryCount}件
                            </Typography>
                          </Box>
                        </Box>
                        <Box>
                          <Typography fontSize={13} color="#888" sx={{ mb: 0.3 }}>
                            時給
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <TrendingUp sx={{ fontSize: 16, color: '#F59E0B' }} />
                            <Typography fontSize={17} fontWeight="bold" color="#F59E0B">
                              {formatCurrency(entry.hourlyRate)}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>

                      {/* 投票ボタン */}
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                          size="small"
                          startIcon={<ThumbUp />}
                          onClick={() => entry.id && handleVote(entry.id, 'good')}
                          disabled={votedRecords.has(entry.id || '')}
                          sx={{
                            color: votedRecords.get(entry.id || '') === 'good' ? '#10B981' : '#666',
                            fontSize: 11,
                            minWidth: 'auto',
                            bgcolor: votedRecords.get(entry.id || '') === 'good' ? '#10B98120' : 'transparent',
                            '&:hover': { bgcolor: '#10B98120' }
                          }}
                        >
                          {entry.goodCount}
                        </Button>
                        <Button
                          size="small"
                          startIcon={<ThumbDown />}
                          onClick={() => entry.id && handleVote(entry.id, 'bad')}
                          disabled={votedRecords.has(entry.id || '')}
                          sx={{
                            color: votedRecords.get(entry.id || '') === 'bad' ? '#EF4444' : '#666',
                            fontSize: 11,
                            minWidth: 'auto',
                            bgcolor: votedRecords.get(entry.id || '') === 'bad' ? '#EF444420' : 'transparent',
                            '&:hover': { bgcolor: '#EF444420' }
                          }}
                        >
                          {entry.badCount}
                        </Button>
                      </Box>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            );
          })}
        </Box>
      )}

      {/* 規約ダイアログ */}
      <Dialog
        open={showTerms}
        onClose={() => setShowTerms(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { bgcolor: '#1a1a1a', color: 'white', borderRadius: '20px' } }}
      >
        <DialogTitle>
          <Typography variant="h6" fontWeight="bold">ランキング機能 利用規約</Typography>
        </DialogTitle>
        <DialogContent>
          <Typography fontSize={16} fontWeight="bold" sx={{ mb: 2, color: '#10B981' }}>
            以下の内容をよくお読みください
          </Typography>
          
          <Box sx={{ mb: 2 }}>
            <Typography fontSize={15} fontWeight="bold" sx={{ mb: 1 }}>
              📊 公開される情報
            </Typography>
            <Typography fontSize={14} color="#ccc" sx={{ pl: 2, mb: 0.5 }}>
              • あなたの設定した表示名（ニックネーム）
            </Typography>
            <Typography fontSize={14} color="#ccc" sx={{ pl: 2, mb: 0.5 }}>
              • 活動地域（都道府県）
            </Typography>
            <Typography fontSize={14} color="#ccc" sx={{ pl: 2, mb: 0.5 }}>
              • 配達の売上金額、件数、時給
            </Typography>
            <Typography fontSize={14} color="#ccc" sx={{ pl: 2, mb: 0.5 }}>
              • 使用しているプラットフォーム名
            </Typography>
            <Typography fontSize={14} color="#ccc" sx={{ pl: 2 }}>
              • 配達実績（日別・プラットフォーム別）
            </Typography>
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography fontSize={15} fontWeight="bold" sx={{ mb: 1 }}>
              🔒 非公開の情報
            </Typography>
            <Typography fontSize={14} color="#ccc" sx={{ pl: 2, mb: 0.5 }}>
              • メールアドレス
            </Typography>
            <Typography fontSize={14} color="#ccc" sx={{ pl: 2, mb: 0.5 }}>
              • 個人を特定できる情報
            </Typography>
            <Typography fontSize={14} color="#ccc" sx={{ pl: 2 }}>
              • 配達場所や詳細なルート
            </Typography>
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography fontSize={15} fontWeight="bold" sx={{ mb: 1 }}>
              ⚡ 自動送信について
            </Typography>
            <Typography fontSize={14} color="#F59E0B" sx={{ pl: 2, mb: 0.5 }}>
              • 機能を有効にすると、既存の全配達記録が自動的にランキングに送信されます
            </Typography>
            <Typography fontSize={14} color="#ccc" sx={{ pl: 2, mb: 0.5 }}>
              • 今後追加する記録も自動で反映されます
            </Typography>
            <Typography fontSize={14} color="#ccc" sx={{ pl: 2 }}>
              • 手動で送信する必要はありません
            </Typography>
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography fontSize={15} fontWeight="bold" sx={{ mb: 1 }}>
              🛡️ 不正防止機能
            </Typography>
            <Typography fontSize={14} color="#ccc" sx={{ pl: 2, mb: 0.5 }}>
              • 他のユーザーがグッド👍またはバッド👎を投票できます
            </Typography>
            <Typography fontSize={14} color="#EF4444" sx={{ pl: 2, mb: 0.5 }}>
              • バッド投票が5回以上でランキングから自動除外されます
            </Typography>
            <Typography fontSize={14} color="#ccc" sx={{ pl: 2 }}>
              • 不正確な記録は他のユーザーにより排除されます
            </Typography>
          </Box>

          <Alert severity="warning" sx={{ bgcolor: '#2a1a0a', color: 'white', mt: 2 }}>
            <Typography fontSize={14} fontWeight="bold" sx={{ mb: 0.5 }}>
              ⚠️ 注意事項
            </Typography>
            <Typography fontSize={13}>
              • 一度公開した記録は他のユーザーに閲覧されます
              <br />
              • 明らかに不正な記録は通報される可能性があります
              <br />
              • 規約に違反する行為は利用停止の対象となります
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowTerms(false)} sx={{ color: '#888' }}>
            キャンセル
          </Button>
          <Button onClick={handleAgreeTerms} sx={{ color: '#10B981', fontWeight: 'bold' }}>
            同意して続ける
          </Button>
        </DialogActions>
      </Dialog>


      {/* ランキング設定ダイアログ */}
      <Dialog
        open={showSettings}
        onClose={() => setShowSettings(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { bgcolor: '#1a1a1a', color: 'white', borderRadius: '20px' } }}
      >
        <DialogTitle>
          <Typography variant="h6" fontWeight="bold">ランキング設定</Typography>
        </DialogTitle>
        <DialogContent>
          <FormControlLabel
            control={
              <Switch
                checked={tempEnabled}
                onChange={(e) => setTempEnabled(e.target.checked)}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': { color: '#10B981' },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#10B981' }
                }}
              />
            }
            label="ランキング機能を有効にする"
            sx={{ mb: 2 }}
          />
          
          {tempEnabled && (
            <>
              <TextField
                fullWidth
                label="表示名"
                value={tempUserName}
                onChange={(e) => setTempUserName(e.target.value)}
                placeholder="ニックネーム"
                sx={{
                  mb: 2,
                  '& .MuiOutlinedInput-root': {
                    color: 'white',
                    '& fieldset': { borderColor: '#333' },
                    '&:hover fieldset': { borderColor: '#10B981' },
                    '&.Mui-focused fieldset': { borderColor: '#10B981' }
                  },
                  '& .MuiInputLabel-root': { color: '#888' }
                }}
              />
              <TextField
                fullWidth
                select
                label="地域"
                value={tempRegion}
                onChange={(e) => setTempRegion(e.target.value)}
                SelectProps={{
                  native: true,
                }}
                sx={{
                  mb: 2,
                  '& .MuiOutlinedInput-root': {
                    color: 'white',
                    '& fieldset': { borderColor: '#333' },
                    '&:hover fieldset': { borderColor: '#10B981' },
                    '&.Mui-focused fieldset': { borderColor: '#10B981' }
                  },
                  '& .MuiInputLabel-root': { color: '#888' }
                }}
              >
                <option value="">選択してください</option>
                {regions.map((region) => (
                  <option key={region} value={region}>
                    {region}
                  </option>
                ))}
              </TextField>
              <Alert severity="success" sx={{ bgcolor: '#0a2318', color: 'white' }}>
                <Typography fontSize={14}>
                  有効化すると、既存の全記録が自動的にランキングに送信されます。
                </Typography>
              </Alert>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowSettings(false)} sx={{ color: '#888' }}>
            キャンセル
          </Button>
          <Button onClick={handleSaveSettings} sx={{ color: '#10B981' }}>
            保存
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default RankingScreen;
